import React, { useState, useEffect } from "react";
import { Button, useMediaQuery } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Toast from "../../utils/Toast";
import apiList from "../../api/apiList";
import { postRequest } from "../../api/apiServices";
import { useAuth } from "../../hooks/useAuth";
import Loader from "../../components/Loader";
import AuthShell from "../../components/Auth/AuthShell";
import AuthInput from "../../components/Auth/AuthInput";

const SignUp: React.FC = () => {
  const { storeUser } = useAuth();
  const [name, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userNameError, setUserNameError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [passwordMismatch, setPasswordMismatch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Sign Up - Sprooze";
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setUserNameError("");
      setEmailError("");
      setPasswordError("");
      setConfirmPasswordError("");
      setPasswordMismatch("");

      if (!name || !email || !password || !confirmPassword) {
        if (!name) setUserNameError("UserName is required");
        if (!email) setEmailError("Email is required");
        if (!password) setPasswordError("Password is required");
        if (!confirmPassword)
          setConfirmPasswordError("Password confirmation is required");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Invalid email address");
        return;
      }

      if (password.length < 8) {
        setPasswordError("Password must be at least 8 characters long");
        return;
      }

      if (password !== confirmPassword) {
        setPasswordMismatch("Passwords do not match");
        return;
      }

      setLoading(true);
      const response: any = await postRequest(apiList.register, {
        name,
        email,
        password,
      });
      if (response) {
        const { user, token } = response.data.data;
        handleUserNavigation(user, token);
        Toast("Please Check Your Email");
        navigate("/emailverify", { state: { email: email } });
      }
    } catch (error) {
      console.error("Error - Register new account.. " + error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserNavigation = (user: any, token: string) => {
    const userData = {
      name: user?.name,
      email: user?.email,
      profile: user?.profile,
      status: user?.status,
      token: token,
      uId: user?.uId,
      isEmailVerify: user?.isEmailVerify ?? false,
    };
    storeUser(userData);
  };

  return (
    <AuthShell
      eyebrow="Get started"
      title="Create your account"
      subtitle="Join Sprooze and transform the way you recruit top talent."
      heroHeading="Start hiring with confidence"
      heroText="Create your account and unlock powerful tools for job posting, candidate management, and campaign outreach."
      isMobile={isMobile}
      footer={
        <>
          Already have an account?
          <Link to="/login" className="auth-footer-link">
            Sign In
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <AuthInput
          label="Full Name"
          placeholder="Enter your name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(value) => {
            setUserName(value);
            setUserNameError("");
          }}
          error={userNameError}
        />

        <AuthInput
          label="Email Address"
          placeholder="you@company.com"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setEmailError("");
          }}
          error={emailError}
        />

        <AuthInput
          label="Password"
          placeholder="Create a password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setPasswordError("");
          }}
          error={passwordError}
        />

        <AuthInput
          label="Confirm Password"
          placeholder="Confirm your password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(value) => {
            setConfirmPassword(value);
            setConfirmPasswordError("");
            setPasswordMismatch("");
          }}
          error={confirmPasswordError || passwordMismatch}
        />

        <Button
          variant="contained"
          type="submit"
          className="auth-submit-btn"
          disabled={loading}
        >
          {loading ? <Loader /> : "Create Account"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default SignUp;
